import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import toast from 'react-hot-toast';
import { ArrowRight, ArrowLeft, Check, DollarSign, LogOut } from 'lucide-react';
import Logo from './Logo';
import './Onboarding.css';
import { apiRequest } from '../config/api';
import StripeCheckout from './StripeCheckout';
import CouponField from './CouponField';

function Onboarding() {
  const navigate = useNavigate();
  const { currentUser, userProfile, updateUserProfile, handlePostPaymentUpdate, logout } = useAuth();
  const [step, setStep] = useState(1);
  const [showCheckout, setShowCheckout] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availableMatches, setAvailableMatches] = useState([]);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [thresholds, setThresholds] = useState({});
  const [frequency, setFrequency] = useState(60);
  const [plan, setPlan] = useState('basico'); // Asegurar que tenga un valor por defecto
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // Verificar si el usuario ya completó el onboarding
  useEffect(() => {
    if (userProfile && userProfile.onboardingCompletado) {
      navigate('/dashboard');
    }
  }, [userProfile, navigate]);

  // Actualizar frecuencia cuando cambie el plan
  useEffect(() => {
    const getDefaultFrequency = () => {
      switch (plan) {
        case 'basico':
          return 60; // 1 hora
        case 'premium':
          return 10; // 10 minutos
        case 'elite':
          return 5; // 5 minutos
        default:
          return 60;
      }
    };
    setFrequency(getDefaultFrequency());
  }, [plan]);

  // Cargar partidos disponibles
  useEffect(() => {
    const fetchMatches = async () => {
      try {
        // Usar endpoint de tickets (colección ticket_prices)
        const matches = await apiRequest('/api/tickets/matches');
        setAvailableMatches(matches);
      } catch (error) {
        console.error('Error cargando partidos:', error);
        toast.error('Error al cargar partidos disponibles');
      }
    };

    fetchMatches();
  }, []);

  const handleMatchSelect = (match) => {
    const isSelected = selectedMatches.find(m => m.matchNo === match.matchNo);
    if (isSelected) {
      setSelectedMatches(selectedMatches.filter(m => m.matchNo !== match.matchNo));
    } else {
      // Validar límite según plan
      const maxMatches = plan === 'basico' ? 1 : plan === 'premium' ? 3 : 7;
      if (selectedMatches.length < maxMatches) {
        setSelectedMatches([...selectedMatches, match]);
      } else {
        toast.error(`Tu plan permite máximo ${maxMatches} partidos`);
      }
    }
  };

  const handleThresholdChange = (matchNo, value) => {
    setThresholds({
      ...thresholds,
      [matchNo]: parseFloat(value) || 0
    });
  };

  const handleCouponApplied = (couponData) => {
    setAppliedCoupon(couponData);
  };

  const handleCouponRemoved = () => {
    setAppliedCoupon(null);
  };

  const handleNext = () => {
    if (step === 1) {
      // En el paso 1 solo validamos que se haya seleccionado un plan
      if (!plan || !['basico', 'premium', 'elite'].includes(plan)) {
        toast.error('Debes seleccionar un plan válido');
        return;
      }
    } else if (step === 2) {
      // En el paso 2 validamos que se hayan seleccionado partidos
      if (selectedMatches.length === 0) {
        toast.error('Debes seleccionar al menos un partido');
        return;
      }
    } else if (step === 3) {
      // En el paso 3 validamos que se hayan configurado umbrales para TODOS los partidos
      const allThresholdsSet = selectedMatches.every(match => 
        thresholds[match.matchNo] && thresholds[match.matchNo] > 0
      );
      if (!allThresholdsSet) {
        toast.error('Debes configurar umbrales de precio para todos los partidos seleccionados');
        return;
      }
    }
    
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleFinish = async () => {
    if (!currentUser) {
      toast.error('Usuario no autenticado');
      return;
    }

    setLoading(true);
    try {
      // Preparar configuración
      const configuracion = {
        partidos: selectedMatches.map(match => ({
          matchNo: match.matchNo,
          umbral: thresholds[match.matchNo] || 0,
          teams: match.teams,
          roundInfo: match.roundInfo
        })),
        frecuencia: frequency,
        activo: true
      };

      // Guardar configuración temporalmente
      await updateDoc(doc(db, 'users', currentUser.uid), {
        plan,
        configuracion,
        onboardingCompletado: false, // No completado hasta que pague
        actualizado: new Date()
      });

      // Actualizar el contexto del usuario
      await updateUserProfile(currentUser.uid);
      
      // Mostrar checkout
      setShowCheckout(true);
    } catch (error) {
      console.error('Error guardando configuración:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    console.log('🎉 handlePaymentSuccess ejecutado');
    console.log('👤 Usuario actual:', currentUser?.uid);
    
    try {
      console.log('📝 Actualizando Firebase...');
      
      // Marcar onboarding como completado
      await updateDoc(doc(db, 'users', currentUser.uid), {
        onboardingCompletado: true,
        actualizado: new Date()
      });

      console.log('✅ Firebase actualizado exitosamente');

      // Actualizar el contexto del usuario con manejo especial post-pago
      console.log('🔄 Actualizando contexto del usuario...');
      await handlePostPaymentUpdate(currentUser.uid);

      console.log('✅ Contexto actualizado');

      // Ocultar checkout
      setShowCheckout(false);
      
      toast.success('¡Pago exitoso! Configuración completada.');
      
      console.log('🚀 Redirigiendo al dashboard...');
      // Redirigir al dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('❌ Error completando onboarding:', error);
      console.error('❌ Stack trace:', error.stack);
      toast.error('Error al completar configuración');
    }
  };

  const handlePaymentCancel = () => {
    setShowCheckout(false);
    toast('Pago cancelado. Puedes intentar de nuevo.');
  };

  const getPlanLimits = () => {
    switch (plan) {
      case 'basico':
        return { matches: 1, frequency: 60, price: 199 };
      case 'premium':
        return { matches: 3, frequency: 10, price: 399 };
      case 'elite':
        return { matches: 7, frequency: 5, price: 599 };
      default:
        return { matches: 1, frequency: 60, price: 199 };
    }
  };

  const limits = getPlanLimits();

  // Obtener opciones de frecuencia según el plan
  const getFrequencyOptions = () => {
    switch (plan) {
      case 'basico':
        return [
          { value: 60, label: 'Monitoreo cada hora', disabled: false }
        ];
      case 'premium':
        return [
          { value: 60, label: 'Cada hora (60 min)', disabled: false },
          { value: 30, label: 'Cada 30 minutos', disabled: false },
          { value: 10, label: 'Monitoreo cada 10 min', disabled: false }
        ];
      case 'elite':
        return [
          { value: 60, label: 'Cada hora (60 min)', disabled: false },
          { value: 30, label: 'Cada 30 minutos', disabled: false },
          { value: 10, label: 'Cada 10 minutos', disabled: false },
          { value: 5, label: 'Monitoreo cada 5 min', disabled: false }
        ];
      default:
        return [
          { value: 60, label: 'Monitoreo cada hora', disabled: false }
        ];
    }
  };

  const frequencyOptions = getFrequencyOptions();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      toast.error('Error al cerrar sesión');
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div className="onboarding-header">
          <div className="header-top">
            <Logo size="auth-logo" className="auth-logo" />
            <div className="user-info">
              <span className="user-greeting">
                ¡Hola, {userProfile?.username || currentUser?.displayName || 'Usuario'}!
              </span>
              <button 
                className="logout-btn"
                onClick={handleLogout}
                title="Cerrar sesión"
              >
                <LogOut size={18} />
                Salir
              </button>
            </div>
          </div>
          <p>Configura tu monitoreo personalizado</p>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(step / 3) * 100}%` }}
            ></div>
          </div>
          <span className="step-indicator">Paso {step} de 3</span>
        </div>

        {step === 1 && (
          <div className="onboarding-step">
            <h2>🎯 Selecciona tu Plan</h2>
            <p>Elige el plan que mejor se adapte a tus necesidades</p>
            <p style={{ 
              marginBottom: '1.5rem', 
              fontSize: '0.9rem', 
              color: '#10b981', 
              fontWeight: '600',
              backgroundColor: '#f0fdf4',
              padding: '0.75rem',
              borderRadius: '0.5rem',
              border: '1px solid #bbf7d0',
              textAlign: 'center'
            }}>
              💳 Todos los planes son pagos únicos (sin suscripciones recurrentes)
            </p>
            
            <div className="plans-grid">
              <div 
                className={`plan-card ${plan === 'basico' ? 'selected' : ''}`}
                onClick={() => setPlan('basico')}
              >
                <h3>Plan Básico</h3>
                <div className="plan-price">$100 MXN</div>
                <ul>
                  <li>1 partido</li>
                  <li>Monitoreo cada hora</li>
                  <li>Alertas por email</li>
                </ul>
              </div>
              
              <div 
                className={`plan-card ${plan === 'premium' ? 'selected' : ''}`}
                onClick={() => setPlan('premium')}
              >
                <h3>Plan Premium</h3>
                <div className="plan-price">$200 MXN</div>
                <ul>
                  <li>Hasta 3 partidos</li>
                  <li>Monitoreo cada 10 min</li>
                  <li>Alertas por email</li>
                  <li>Dashboard avanzado</li>
                </ul>
              </div>
              
              <div 
                className={`plan-card ${plan === 'elite' ? 'selected' : ''}`}
                onClick={() => setPlan('elite')}
              >
                <h3>Plan Elite</h3>
                <div className="plan-price">$400 MXN</div>
                <ul>
                  <li>Hasta 7 partidos</li>
                  <li>Monitoreo cada 5 min</li>
                  <li>Alertas por email</li>
                  <li>Dashboard completo</li>
                  <li>Soporte prioritario</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-step">
            <h2>⚽ Selecciona tus Partidos</h2>
            <p>Elige hasta {limits.matches} partidos para monitorear</p>
            <p style={{ marginTop: 8, fontSize: 13, color: '#ffd700' }}>
              El precio resaltado en amarillo corresponde al precio actual más bajo disponible actualmente para el respectivo partido.
            </p>
            <p style={{ marginTop: 8, fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
              💰 Todos los precios mostrados están en dólares (USD)
            </p>
            
            <div className="matches-grid">
              {availableMatches.map((match) => {
                const isSelected = selectedMatches.find(m => m.matchNo === match.matchNo);
                return (
                  <div 
                    key={match.matchNo}
                    className={`match-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleMatchSelect(match)}
                    style={{
                      minHeight: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      backgroundColor: 'var(--bg-card)',
                      border: '2px solid var(--border-primary)'
                    }}
                  >
                    <div className="match-header">
                      <span className="match-number">M{match.matchNo}</span>
                      {isSelected && <Check size={20} className="check-icon" />}
                    </div>
                    <div className="match-content">
                      <h3 style={{ 
                        color: 'var(--accent-blue)', 
                        margin: '0 0 0.5rem 0',
                        fontSize: '1rem',
                        fontWeight: '700',
                        textAlign: 'center'
                      }}>
                        {match.teams}
                      </h3>
                      <p style={{ 
                        color: 'var(--text-secondary)', 
                        fontSize: '0.875rem',
                        margin: '0 0 0.25rem 0'
                      }}>
                        {match.roundInfo}
                      </p>
                      <p style={{ 
                        color: 'var(--text-secondary)', 
                        fontSize: '0.875rem',
                        margin: '0 0 0.5rem 0'
                      }}>
                        {match.stadium}
                      </p>
                    </div>
                    <div className="match-price" style={{
                      marginTop: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      color: 'var(--accent-gold)',
                      fontWeight: '700',
                      fontSize: '1rem'
                    }}>
                      <DollarSign size={16} />
                      {match.floorPrice.toLocaleString()}
                      <span style={{
                        marginLeft: 8,
                        background: 'var(--accent-gold)',
                        color: '#1f2937',
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 700
                      }}>
                        {match.floorTicketCount === 1 ? '1 ticket' : `${match.floorTicketCount} tickets`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="onboarding-step">
            <h2>💰 Configura tus Umbrales</h2>
            <p>Establece el precio mínimo para recibir alertas</p>
            <p style={{ marginTop: -12, marginBottom: 20, color: '#9ca3af', fontSize: 14, lineHeight: 1.5 }}>
              Imagina que pones una alarma. Aquí pones una "alarma de precio": si el precio
              baja del número que escribes, te avisamos por correo. Revisamos los precios
              según tu selección.
            </p>
            
            <div className="thresholds-section">
              {selectedMatches.map((match) => (
                <div key={match.matchNo} className="threshold-item">
                  <div className="match-info">
                    <h4>M{match.matchNo} - {match.teams}</h4>
                    <p>Precio actual: ${match.floorPrice.toLocaleString()}</p>
                  </div>
                  <div className="threshold-input">
                    <label>
                      Umbral de alerta ($): 
                      <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      value={thresholds[match.matchNo] || ''}
                      onChange={(e) => handleThresholdChange(match.matchNo, e.target.value)}
                      placeholder="Ej: 600"
                      min="0"
                      step="0.01"
                      className={!thresholds[match.matchNo] || thresholds[match.matchNo] <= 0 ? 'error' : ''}
                    />
                    {(!thresholds[match.matchNo] || thresholds[match.matchNo] <= 0) && (
                      <span className="error-message">Este campo es obligatorio</span>
                    )}
                  </div>
                </div>
              ))}
              
              <div className="frequency-section">
                <h4>Frecuencia de Monitoreo</h4>
                <div className="frequency-options">
                  {frequencyOptions.map((option) => (
                    <label key={option.value}>
                      <input
                        type="radio"
                        value={option.value}
                        checked={frequency === option.value}
                        onChange={(e) => setFrequency(parseInt(e.target.value))}
                        disabled={option.disabled}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="onboarding-actions">
          {step > 1 && (
            <button 
              className="btn btn-secondary" 
              onClick={handleBack}
              disabled={loading}
            >
              <ArrowLeft size={18} />
              Anterior
            </button>
          )}
          
          {step < 3 ? (
            <button 
              className="btn btn-primary" 
              onClick={handleNext}
              disabled={loading}
            >
              Siguiente
              <ArrowRight size={18} />
            </button>
          ) : (
            <button 
              className="btn btn-primary" 
              onClick={handleFinish}
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Completar Configuración'}
            </button>
          )}
        </div>
      </div>

      {/* Campo de Cupón */}
      {showCheckout && (
        <div className="checkout-section">
          <div className="checkout-header">
            <h2>💳 Finalizar Compra</h2>
            <p>Completa tu pago para activar tu plan</p>
          </div>
          
          <div className="plan-summary">
            <div className="plan-info">
              <h3>Plan {plan.charAt(0).toUpperCase() + plan.slice(1)}</h3>
              <p className="plan-description">
                {plan === 'basico' && '1 partido, monitoreo cada hora'}
                {plan === 'premium' && 'Hasta 3 partidos, monitoreo cada 10 min'}
                {plan === 'elite' && 'Hasta 7 partidos, monitoreo cada 5 min'}
              </p>
            </div>
            <div className="plan-price">
              <span className="original-price">
                ${plan === 'basico' ? '100' : plan === 'premium' ? '200' : '400'} MXN
              </span>
              {appliedCoupon && (
                <span className="final-price">
                  ${appliedCoupon.finalPrice.toLocaleString()} MXN
                </span>
              )}
            </div>
          </div>

          <CouponField
            selectedPlan={plan}
            onCouponApplied={handleCouponApplied}
            onCouponRemoved={handleCouponRemoved}
          />

          <StripeCheckout
            plan={plan}
            onSuccess={handlePaymentSuccess}
            onCancel={handlePaymentCancel}
            appliedCoupon={appliedCoupon}
          />
        </div>
      )}
    </div>
  );
}

export default Onboarding;
